import { DebtType, loan_application } from '@axo/shared/data-access/types';
import { useCallback, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { debtIsFilled } from '../../../../config/utils/debtIsFilled';

export const useDebts = () => {
  const { trigger } = useFormContext();

  const { fields, append, prepend, remove } = useFieldArray({
    name: 'LoanApplication/Debts',
  });

  const debts = useWatch({ name: 'LoanApplication/Debts' });

  const [editingDebtIndex, setEditingDebtIndex] = useState(() => {
    return debtIsFilled(debts?.at(-1)) ? -1 : debts?.length - 1;
  });

  /**
      Fields from fieldArray only contains id for internal use
      injects values from LoanApplication/Debts into the array
     * @see https://react-hook-form.com/docs/usefieldarray #Controlled Field Array
     */
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...debts?.[index],
    };
  });

  const editDebt = useCallback(
    (index: number) => {
      if (controlledFields.some((debt) => !debtIsFilled(debt))) {
        trigger('LoanApplication/Debts');
        return;
      }

      if (editingDebtIndex === index) {
        setEditingDebtIndex(-1);
        return;
      }
      setEditingDebtIndex(index);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debts, editingDebtIndex]
  );

  const addDebt = useCallback(
    (type?: DebtType) => {
      const latestDebt = controlledFields
        ?.filter((debt) => debt.Type !== loan_application.DebtType.Collection)
        ?.at(-1);

      if (type === DebtType.Collection) {
        prepend({
          Type: DebtType.Collection,
          OutstandingAmount: null,
          MonthlyDebtPayment: null,
          Debtor: loan_application.Debtor.MainApplicant,
        });

        setEditingDebtIndex((state) => state + 1);
        return;
      }

      if (!debtIsFilled(latestDebt)) {
        trigger('LoanApplication/Debts');
        return;
      }

      append({
        Type: type,
        Creditor: null,
        OutstandingAmount: null,
        MonthlyDebtPayment: null,
        Debtor: loan_application.Debtor.MainApplicant,
      });

      setEditingDebtIndex(controlledFields.length);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debts]
  );

  const removeDebt = useCallback((index: number, type?: DebtType) => {
    if (index === 0 && type === DebtType.Collection) {
      const firstDebt = debts?.at(0);

      if (firstDebt?.Type !== DebtType.Collection) {
        return;
      }

      remove(index);
      setEditingDebtIndex((state) => state - 1);
      return;
    }

    remove(index);
    setEditingDebtIndex(-1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    debts: controlledFields,
    editingDebtIndex,
    editDebt,
    addDebt,
    removeDebt,
  };
};
